import { z } from "zod";
import Ajv2020 from "ajv/dist/2020.js";
import { isSupportedTimeZone } from "./calendar-time.js";
import { channelObjectFieldTypeSchema } from "./channel-field-types.js";
import { workflowNodeSchema } from "./workflow-nodes.js";
import { workflowJudgeQuestionPlaceholders, workflowJudgeQuestionSchema } from "./workflow-judge-questions.js";
import { workflowDocumentPatternSpecSchema, workflowReviewPatternSpecSchema } from "./workflow-document-pattern.js";
const key = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/u);
const label = z.string().trim().min(1).max(160);
const resourceId = z.string().trim().min(1).max(200);
const sourceBase = { key, required: z.boolean() };
const scalar = z.union([z.string(), z.number(), z.boolean()]);
const sourceSelectorSchema = z.discriminatedUnion("operator", [
    z.object({ field: key, operator: z.literal("eq"), value: scalar }).strict(),
    z.object({ field: key, operator: z.literal("in"), values: z.array(scalar).min(1).max(100) }).strict(),
]);
const Ajv2020Constructor = Ajv2020;
const schemaValidator = new Ajv2020Constructor({ strict: false });
const outputSchema = z.record(z.string(), z.json()).superRefine((schema, ctx) => {
    if (JSON.stringify(schema).length > 16_384 || !schemaValidator.validateSchema(schema))
        ctx.addIssue({ code: "custom", message: "A valid bounded JSON Schema is required" });
    const containsRef = (value) => value !== null && typeof value === "object" &&
        Object.entries(value).some(([key, child]) => key === "$ref" ||
            (typeof child === "object" && containsRef(child)));
    if (containsRef(schema))
        ctx.addIssue({ code: "custom", message: "Output schemas cannot use references" });
});
const durableWorkflowNodeSchema = workflowNodeSchema.extend({ outputSchema,
    /** Absent property means the judge is not called for this item. */
    judgePayloadKey: key.optional() });
function declaresOutputProperty(schema, property) {
    if (!schema || typeof schema !== "object" || Array.isArray(schema))
        return false;
    const object = schema;
    const properties = object.properties;
    return object.type === "object" && properties !== null &&
        typeof properties === "object" && !Array.isArray(properties) &&
        Object.hasOwn(properties, property);
}
/** A declaration is not a grant. Activation must bind and authorize each live source. */
const workflowSourceSchema = z.discriminatedUnion("kind", [
    z.object({ ...sourceBase, kind: z.literal("connector_entity"), integrationId: resourceId, entity: key,
        relationName: key.optional(), externalIdField: key, subrecordKeyField: key.optional(),
        revisionField: key.optional(),
        titleField: key.optional(), sourceRefField: key.optional(),
        maxStalenessMs: z.number().int().positive().max(30 * 24 * 60 * 60_000).optional(),
        selectors: z.array(sourceSelectorSchema).max(16) }).strict(),
    z.object({ ...sourceBase, kind: z.literal("channel_dataset"), channelId: resourceId,
        objectTypeId: resourceId, datasetId: resourceId.optional() }).strict(),
    z.object({ ...sourceBase, kind: z.literal("file_upload"), channelId: resourceId,
        mimeTypes: z.array(z.enum(["application/pdf", "image/jpeg", "image/png", "image/webp"])).min(1).max(4) }).strict(),
    z.object({ ...sourceBase, kind: z.literal("plugin_read"), pluginSlug: z.string().min(1).max(120),
        toolName: z.string().min(1).max(120), pluginVersion: z.string().min(1).max(120),
        reviewedDefinitionHash: z.string().regex(/^[0-9a-f]{64}$/u) }).strict(),
]);
const sourceMappingSchema = z.object({ sourceKey: key, sourceField: key }).strict();
const workflowFieldSchema = z.object({
    key, label, type: channelObjectFieldTypeSchema, scope: z.enum(["item", "line"]),
    owner: z.enum(["source", "workflow", "reviewer"]),
    /** Fields the data set derives from the workflow's own records instead of storing: the review decision, the
     * processing state, and the review status in words (decision and posting together). */
    projection: z.enum(["review_state", "processing_state", "review_status"]).optional().describe("Derive this field from the workflow's own records. review_state and processing_state are never written. "
        + "review_status is the item's status in words: declare it on the item's status field. The workflow may write its "
        + "own wording there, which stands while the item's review is open or the item was processed with no review; once a "
        + "person decides, or while the item is processing, failed or was skipped, the engine's words replace it (approved, "
        + "posted, declined, processing failed, skipped). Show one status per view, never both."),
    config: z.record(z.string(), z.unknown()).optional().describe("The field's settings, as for a channel field. A percent field holding how sure a judgement is (Twin Judge's "
        + "confidence) sets display to confidence, and percentScale to fraction for 0-1 values, to show as a coloured badge."),
    sourceMappings: z.array(sourceMappingSchema).max(32),
}).strict();
const workflowDatasetSourceSchema = z.discriminatedUnion("role", [
    z.object({ sourceKey: key, role: z.literal("root") }).strict(),
    z.object({ sourceKey: key, role: z.literal("lookup") }).strict(),
    z.object({ sourceKey: key, role: z.enum(["related", "evidence"]),
        parentSourceKey: key, parentExternalIdField: key }).strict(),
]);
const workflowDatasetSchema = z.object({
    key, name: label, channelId: resourceId,
    existingDatasetId: resourceId.optional(), existingObjectTypeId: resourceId.optional(),
    intakeFileFieldKey: key.optional(),
    /**
     * A reviewer-owned json field holding corrections as { document?: { [path]: value },
     * fields?: { [fieldKey]: value } }. A reviewer writes it from the review; the edit re-runs
     * the item, which reads it in its input values.
     */
    correctionsFieldKey: key.optional(),
    /**
     * A workflow-owned date field the workflow sets to say when an item should be looked at again: an invoice that
     * falls due, a follow-up date. On that day the workflow's daily trigger evaluates the item again, as a new
     * revision, though its source has not changed.
     */
    recheckFieldKey: key.optional(),
    sources: z.array(workflowDatasetSourceSchema).min(1).max(32),
    fields: z.array(workflowFieldSchema).min(1).max(100),
}).strict();
const viewFilterSchema = z.discriminatedUnion("operator", [
    z.object({ fieldKey: key, operator: z.enum(["eq", "neq"]), value: scalar }).strict(),
    z.object({ fieldKey: key, operator: z.literal("in"), values: z.array(scalar).min(1).max(50) }).strict(),
    z.object({ fieldKey: key, operator: z.enum(["is_null", "is_not_null"]) }).strict(),
]);
const workflowViewSchema = z.object({
    key, datasetKey: key, name: label, kind: z.literal("table"),
    columns: z.array(key).min(1).max(100), filters: z.array(viewFilterSchema).max(12),
    anyOf: z.array(z.array(viewFilterSchema).min(1).max(4)).min(2).max(4).optional(),
    sort: z.array(z.object({ fieldKey: key, direction: z.enum(["asc", "desc"]) }).strict()).max(1),
}).strict();
export const workflowTriggerSchema = z.discriminatedUnion("kind", [
    z.object({ key, kind: z.literal("source_change"), sourceKey: key }).strict(),
    z.object({ key, kind: z.literal("incremental"), sourceKey: key,
        intervalMinutes: z.number().int().min(5).max(1440),
        /**
         * Evaluate the rows a connector source already holds when the trigger first polls, once, rather than only
         * the rows that change afterwards: the overdue invoices a chaser exists for. A channel source always does.
         */
        backfill: z.boolean().optional() }).strict(),
    z.object({ key, kind: z.literal("daily"), localTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u),
        timeZone: z.string().min(1).max(100).refine(isSupportedTimeZone, "Must be an IANA time zone") }).strict(),
]);
/** Bounded context only; the executor must enforce current access on every query. */
const workflowContextSchema = z.object({
    key, sourceKey: key, matchFields: z.array(key).min(1).max(8), limit: z.number().int().min(1).max(100),
}).strict();
/** Import permission is reviewed with the immutable definition, including the destination. */
export const workflowDocumentIntakeSchema = z.object({
    key, adapter: z.literal("gmail_attachment"), sourceKey: key, uploadSourceKey: key,
    maxBytes: z.number().int().min(1).max(20 * 1024 * 1024),
    /** Only these document types are taken; every supported type when omitted. */
    mimeTypes: z.array(z.enum(["application/pdf", "image/jpeg", "image/png", "image/webp"])).min(1).max(4).optional(),
    /** Only mail received on or after this date is read, as a person picks how far back to start. */
    receivedFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u).optional(),
}).strict();
/** Finite host effects; external posting requires a separately authorized current review. */
export const workflowEffectSchema = z.discriminatedUnion("kind", [
    z.object({ key, kind: z.literal("source_read"), sourceKey: key,
        contextKey: key.optional(),
        /** Ask the engine for the established pattern of the documents whose lines this read returns. */
        pattern: workflowDocumentPatternSpecSchema.optional() }).strict(),
    z.object({ key, kind: z.literal("extract_document"), sourceKey: key,
        provider: z.literal("reducto"), extractionSchema: outputSchema.refine((schema) => schema.type === "object" && !!schema.properties && typeof schema.properties === "object" &&
            !Array.isArray(schema.properties), "Extraction schemas require object properties").optional() }).strict(),
    z.object({ key, kind: z.literal("review_history"), datasetKey: key,
        matchFieldKey: key, limit: z.number().int().min(1).max(20),
        /** Ask the engine for the established pattern of the lines the accepted reviews signed off. */
        pattern: workflowReviewPatternSpecSchema.optional() }).strict(),
    z.object({ key, kind: z.literal("item_upsert"), datasetKey: key,
        fieldKeys: z.array(key).min(1).max(100) }).strict(),
    z.object({ key, kind: z.literal("xero_bill"), datasetKey: key, integrationId: resourceId, reviewEffectKey: key }).strict(),
    z.object({ key, kind: z.literal("proposal_append"), datasetKey: key,
        /** One named reviewer; `reviewers` names several, or routes by a record field. A step declares one or the other. */
        reviewerUserId: resourceId.optional(),
        /**
         * Who reviews the step's cards (docs/specs/shared-review-reviewers.md): named people, any of whom, or an admin,
         * decides, and optionally a `user` field of the dataset whose value reviews that item, with the named people as the
         * fallback. Without either, the activation's approver reviews.
         */
        reviewers: z.object({ userIds: z.array(resourceId).min(1).max(10), fromField: key.optional() }).strict().optional(),
        waitForDecision: z.boolean().optional(),
        timeoutMinutes: z.number().int().min(1).max(43200).optional(),
        /** What Twin Judge is asked about each review before it is raised; see workflowJudgeQuestionSchema. */
        judgeQuestions: z.array(workflowJudgeQuestionSchema).max(16).optional(),
        /**
         * What Accept may write to a record the run read: a channel data set source, and the fields of its records a card
         * may set. Accept writes as the person deciding, through the record write path, so their own access applies, and
         * Undo puts the values back.
         */
        recordWrites: z.object({ sourceKey: key, fieldKeys: z.array(key).min(1).max(8) }).strict().optional() }).strict(),
]);
/** One V2 definition is shared by fixture testing, reviewed activation and durable execution. */
export const durableWorkflowDefinitionSchema = z.object({
    schemaVersion: z.literal(2), title: label, description: z.string().max(4000),
    python: z.string().min(1).max(100_000),
    nodes: z.array(durableWorkflowNodeSchema).min(1).max(64),
    sources: z.array(workflowSourceSchema).min(1).max(64),
    datasets: z.array(workflowDatasetSchema).min(1).max(16),
    views: z.array(workflowViewSchema).max(64),
    triggers: z.array(workflowTriggerSchema).max(32),
    context: z.array(workflowContextSchema).max(32),
    documentIntakes: z.array(workflowDocumentIntakeSchema).max(8).optional(),
    effects: z.array(workflowEffectSchema).max(64),
    effectBindings: z.array(z.object({ nodeId: key, effectKey: key,
        /** Absent property means the effect is not called for this item. */
        payloadKey: key.optional() }).strict()).max(64),
}).strict().superRefine((definition, ctx) => {
    const unique = (items, path) => {
        const seen = new Set();
        items.forEach((item, index) => {
            if (seen.has(item.key))
                ctx.addIssue({ code: "custom", path: [path, index, "key"], message: `Duplicate key '${item.key}'` });
            seen.add(item.key);
        });
        return seen;
    };
    const sources = unique(definition.sources, "sources");
    const datasets = unique(definition.datasets, "datasets");
    const resourceKeys = new Set();
    for (const [kind, items] of [["sources", definition.sources], ["datasets", definition.datasets],
        ["views", definition.views]]) {
        items.forEach((item, index) => {
            if (resourceKeys.has(item.key))
                ctx.addIssue({ code: "custom", path: [kind, index, "key"],
                    message: "Resource keys must be unique across sources, datasets and views" });
            resourceKeys.add(item.key);
        });
    }
    const channelIds = new Set(definition.datasets.map((dataset) => dataset.channelId));
    if (channelIds.size > 1)
        ctx.addIssue({ code: "custom", path: ["datasets"],
            message: "One activation currently binds resources in one channel" });
    definition.sources.forEach((source, index) => {
        if (source.kind === "file_upload" && !channelIds.has(source.channelId))
            ctx.addIssue({ code: "custom", path: ["sources", index, "channelId"],
                message: "Upload source channel must match the dataset channel" });
    });
    unique(definition.views, "views");
    unique(definition.triggers, "triggers");
    unique(definition.context, "context");
    const effects = unique(definition.effects, "effects");
    const nodes = new Set();
    definition.nodes.forEach((node, index) => {
        if (nodes.has(node.id))
            ctx.addIssue({ code: "custom", path: ["nodes", index, "id"], message: `Duplicate node '${node.id}'` });
        if (node.judgePayloadKey && node.kind !== "judge")
            ctx.addIssue({ code: "custom",
                path: ["nodes", index, "judgePayloadKey"], message: "Only judge nodes may condition judgment" });
        if (node.judgePayloadKey && !declaresOutputProperty(node.outputSchema, node.judgePayloadKey))
            ctx.addIssue({ code: "custom", path: ["nodes", index, "judgePayloadKey"],
                message: "Conditional judgment needs a declared output property" });
        if (new Set(node.inputs).size !== node.inputs.length)
            ctx.addIssue({ code: "custom", path: ["nodes", index, "inputs"], message: "Duplicate dependency" });
        for (const input of node.inputs)
            if (!nodes.has(input))
                ctx.addIssue({ code: "custom", path: ["nodes", index, "inputs"], message: `Dependency '${input}' must name an earlier node` });
        nodes.add(node.id);
    });
    const fieldsByDataset = new Map();
    const boundSources = new Set();
    definition.datasets.forEach((dataset, datasetIndex) => {
        const bound = new Set();
        dataset.sources.forEach((binding, index) => {
            if (definition.sources.some((source) => source.key === binding.sourceKey &&
                source.kind === "plugin_read") && binding.role !== "lookup")
                ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "sources", index, "role"],
                    message: "Plugin sources support lookup reads; they cannot create workflow items" });
            if (definition.sources.some(source => source.key === binding.sourceKey && source.kind === "channel_dataset") &&
                binding.role !== "root" && binding.role !== "lookup")
                ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "sources", index, "role"],
                    message: "Channel datasets support root and lookup bindings" });
            if (!sources.has(binding.sourceKey))
                ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "sources", index, "sourceKey"], message: "Unknown source" });
            if (bound.has(binding.sourceKey))
                ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "sources", index, "sourceKey"], message: "Duplicate source binding" });
            if (boundSources.has(binding.sourceKey))
                ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "sources", index, "sourceKey"], message: "A source may bind to only one dataset in this version" });
            if (binding.role !== "root" && binding.role !== "lookup" &&
                !dataset.sources.some((candidate) => candidate.sourceKey === binding.parentSourceKey && candidate.role === "root"))
                ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "sources", index, "parentSourceKey"], message: "Parent must be a root source in this dataset" });
            if (binding.role !== "root" && binding.role !== "lookup" &&
                definition.sources.some(source => source.key === binding.sourceKey && source.kind === "connector_entity") &&
                definition.sources.some(source => source.key === binding.parentSourceKey && source.kind !== "connector_entity"))
                ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "sources", index, "parentSourceKey"],
                    message: "Related connector records require a connector root; use a source read for channel lookups" });
            if (binding.role !== "root" && binding.role !== "lookup" && definition.sources.some((candidate) => candidate.key === binding.parentSourceKey && candidate.kind === "connector_entity" &&
                candidate.subrecordKeyField))
                ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "sources", index, "parentSourceKey"],
                    message: "Related connector records require a root without a subrecord key" });
            bound.add(binding.sourceKey);
            boundSources.add(binding.sourceKey);
        });
        if (!dataset.sources.some((binding) => binding.role === "root"))
            ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "sources"], message: "Dataset requires a root source" });
        const fields = new Map();
        const projections = new Set();
        dataset.fields.forEach((field, index) => {
            const path = ["datasets", datasetIndex, "fields", index];
            if (fields.has(field.key))
                ctx.addIssue({ code: "custom", path: [...path, "key"], message: "Duplicate field key" });
            fields.set(field.key, { owner: field.owner, scope: field.scope });
            if (field.projection) {
                if (field.owner !== "workflow" || field.scope !== "item" ||
                    !["text", "select"].includes(field.type) || projections.has(field.projection))
                    ctx.addIssue({ code: "custom", path: [...path, "projection"],
                        message: "One item-level workflow-owned field is allowed per lifecycle projection" });
                projections.add(field.projection);
            }
            if (field.owner === "source" && field.sourceMappings.length === 0)
                ctx.addIssue({ code: "custom", path: [...path, "sourceMappings"], message: "Source-owned field requires a source mapping" });
            if (field.owner !== "source" && field.sourceMappings.length > 0)
                ctx.addIssue({ code: "custom", path: [...path, "sourceMappings"], message: "Only source-owned fields may have source mappings" });
            const mappings = new Set();
            field.sourceMappings.forEach((mapping, mappingIndex) => {
                if (definition.sources.some((source) => source.key === mapping.sourceKey &&
                    source.kind === "plugin_read"))
                    ctx.addIssue({ code: "custom", path: [...path, "sourceMappings", mappingIndex],
                        message: "Plugin lookup values must be read by a declared source effect" });
                if (definition.sources.some(source => source.key === mapping.sourceKey && source.kind === "channel_dataset") && field.scope !== "item")
                    ctx.addIssue({ code: "custom", path: [...path, "sourceMappings", mappingIndex],
                        message: "Channel dataset sources project item fields" });
                if (definition.sources.some(source => source.key === mapping.sourceKey && source.kind === "channel_dataset") &&
                    !dataset.sources.some(binding => binding.sourceKey === mapping.sourceKey && binding.role === "root"))
                    ctx.addIssue({ code: "custom", path: [...path, "sourceMappings", mappingIndex],
                        message: "Channel lookup values must be read by a declared source effect" });
                if (!bound.has(mapping.sourceKey))
                    ctx.addIssue({ code: "custom", path: [...path, "sourceMappings", mappingIndex, "sourceKey"], message: "Source is not bound to this dataset" });
                const identity = mapping.sourceKey;
                if (mappings.has(identity))
                    ctx.addIssue({ code: "custom", path: [...path, "sourceMappings", mappingIndex], message: "Duplicate source mapping" });
                mappings.add(identity);
            });
        });
        const hasRootUpload = dataset.sources.some((binding) => binding.role === "root" &&
            definition.sources.some((source) => source.key === binding.sourceKey && source.kind === "file_upload"));
        if (hasRootUpload && !dataset.intakeFileFieldKey)
            ctx.addIssue({ code: "custom",
                path: ["datasets", datasetIndex, "intakeFileFieldKey"],
                message: "A file-upload root requires a declared evidence file field" });
        if (dataset.intakeFileFieldKey) {
            const evidence = dataset.fields.find((field) => field.key === dataset.intakeFileFieldKey);
            if (!evidence || evidence.type !== "file" || evidence.owner !== "workflow" || evidence.scope !== "item")
                ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "intakeFileFieldKey"],
                    message: "Intake evidence must be an item-level workflow-owned file field" });
        }
        if (dataset.correctionsFieldKey) {
            const corrections = dataset.fields.find((field) => field.key === dataset.correctionsFieldKey);
            if (!corrections || corrections.type !== "json" || corrections.owner !== "reviewer" || corrections.scope !== "item")
                ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "correctionsFieldKey"],
                    message: "Reviewer corrections must be an item-level reviewer-owned json field" });
        }
        if (dataset.recheckFieldKey) {
            const recheck = dataset.fields.find((field) => field.key === dataset.recheckFieldKey);
            if (!recheck || recheck.type !== "date" || recheck.owner !== "workflow" || recheck.scope !== "item")
                ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "recheckFieldKey"],
                    message: "A recheck date must be an item-level workflow-owned date field" });
            if (!definition.triggers.some((trigger) => trigger.kind === "daily"))
                ctx.addIssue({ code: "custom", path: ["datasets", datasetIndex, "recheckFieldKey"],
                    message: "A recheck date needs a daily trigger to look at items again" });
        }
        fieldsByDataset.set(dataset.key, fields);
    });
    definition.views.forEach((view, index) => {
        const fields = fieldsByDataset.get(view.datasetKey);
        if (!fields)
            ctx.addIssue({ code: "custom", path: ["views", index, "datasetKey"], message: "Unknown dataset" });
        for (const [fieldKey, path] of [
            ...view.columns.map((fieldKey, offset) => [fieldKey, ["columns", offset]]),
            ...view.filters.map((filter, offset) => [filter.fieldKey, ["filters", offset, "fieldKey"]]),
            ...(view.anyOf ?? []).flatMap((group, groupIndex) => group.map((filter, offset) => [filter.fieldKey, ["anyOf", groupIndex, offset, "fieldKey"]])),
            ...view.sort.map((sort, offset) => [sort.fieldKey, ["sort", offset, "fieldKey"]]),
        ]) {
            if (fields && !fields.has(fieldKey))
                ctx.addIssue({ code: "custom", path: ["views", index, ...path], message: `Unknown field '${fieldKey}'` });
            else if (fields?.get(fieldKey)?.scope === "line")
                ctx.addIssue({ code: "custom", path: ["views", index, ...path], message: `Line field '${fieldKey}' cannot be projected on item rows` });
        }
    });
    const intakeKeys = new Set();
    const intakeSources = new Set();
    for (const intake of definition.documentIntakes ?? []) {
        const source = definition.sources.find((entry) => entry.key === intake.sourceKey);
        const upload = definition.sources.find((entry) => entry.key === intake.uploadSourceKey);
        const dataset = definition.datasets.find((entry) => entry.sources.some((binding) => binding.sourceKey === intake.sourceKey && binding.role === "lookup") &&
            entry.sources.some((binding) => binding.sourceKey === intake.uploadSourceKey && binding.role === "root"));
        if (intakeSources.has(intake.sourceKey) || intakeKeys.has(intake.key) || source?.kind !== "connector_entity" || !source.revisionField ||
            source.entity !== "message" || upload?.kind !== "file_upload" || !dataset ||
            !definition.triggers.some((trigger) => trigger.kind === "incremental" && trigger.sourceKey === intake.sourceKey))
            ctx.addIssue({ code: "custom", path: ["documentIntakes"], message: "Intake needs unique identity, a revisioned message lookup, same-dataset upload root and incremental trigger" });
        intakeKeys.add(intake.key);
        intakeSources.add(intake.sourceKey);
    }
    definition.triggers.forEach((trigger, index) => {
        if (trigger.kind !== "daily" && !sources.has(trigger.sourceKey))
            ctx.addIssue({ code: "custom", path: ["triggers", index, "sourceKey"], message: "Unknown source" });
        const source = trigger.kind === "daily" ? undefined
            : definition.sources.find((candidate) => candidate.key === trigger.sourceKey);
        if (trigger.kind === "source_change" && source && source.kind !== "file_upload")
            ctx.addIssue({ code: "custom", path: ["triggers", index, "sourceKey"],
                message: "Source change events are supported only for file uploads; use incremental polling for connector sources" });
        if (trigger.kind === "incremental") {
            if (source && source.kind !== "connector_entity" && source.kind !== "channel_dataset")
                ctx.addIssue({ code: "custom", path: ["triggers", index, "sourceKey"],
                    message: "Incremental polling requires a connector or channel dataset source" });
            if (source?.kind === "channel_dataset" && !definition.datasets.some(dataset => dataset.sources.some(binding => binding.sourceKey === source.key && binding.role === "root")))
                ctx.addIssue({ code: "custom", path: ["triggers", index, "sourceKey"],
                    message: "Channel dataset polling requires a root binding" });
            if (source?.kind === "connector_entity" && !source.revisionField)
                ctx.addIssue({ code: "custom", path: ["triggers", index, "sourceKey"],
                    message: "Connector polling requires a declared source revision field" });
            if (source?.kind === "connector_entity" && !definition.documentIntakes?.some((intake) => intake.sourceKey === source.key) && !definition.datasets.some((dataset) => dataset.sources.some((binding) => binding.sourceKey === source.key &&
                (binding.role === "root" || binding.role === "related" || binding.role === "evidence"))))
                ctx.addIssue({ code: "custom", path: ["triggers", index, "sourceKey"],
                    message: "Connector polling requires a root or related source binding" });
        }
    });
    definition.context.forEach((query, index) => {
        if (!sources.has(query.sourceKey))
            ctx.addIssue({ code: "custom", path: ["context", index, "sourceKey"], message: "Unknown source" });
        const source = definition.sources.find((candidate) => candidate.key === query.sourceKey);
        if (source && (source.kind !== "connector_entity" || !source.revisionField))
            ctx.addIssue({ code: "custom", path: ["context", index, "sourceKey"],
                message: "Historical context requires a revisioned connector source" });
        if (new Set(query.matchFields).size !== query.matchFields.length)
            ctx.addIssue({ code: "custom", path: ["context", index, "matchFields"],
                message: "Duplicate context match field" });
    });
    definition.effects.forEach((effect, index) => {
        if (effect.kind === "xero_bill") {
            const review = definition.effects.find(candidate => candidate.key === effect.reviewEffectKey);
            if (review?.kind !== "proposal_append" || !review.waitForDecision || review.datasetKey !== effect.datasetKey)
                ctx.addIssue({ code: "custom", path: ["effects", index], message: "Xero bills require an explicit waiting review on the same dataset" });
        }
        if (effect.kind === "proposal_append" && effect.timeoutMinutes && !effect.waitForDecision)
            ctx.addIssue({ code: "custom", path: ["effects", index], message: "Review timeouts require a waiting review" });
        if (effect.kind === "proposal_append" && effect.recordWrites) {
            const writes = effect.recordWrites;
            if (definition.sources.find((source) => source.key === writes.sourceKey)?.kind !== "channel_dataset")
                ctx.addIssue({ code: "custom", path: ["effects", index, "recordWrites", "sourceKey"],
                    message: "A card writes only to records of a channel data set source the workflow reads" });
            if (effect.waitForDecision)
                ctx.addIssue({ code: "custom", path: ["effects", index, "recordWrites"],
                    message: "A card that writes on Accept does not also wait to continue the run" });
            if (new Set(writes.fieldKeys).size !== writes.fieldKeys.length)
                ctx.addIssue({ code: "custom", path: ["effects", index, "recordWrites", "fieldKeys"], message: "Each field is named once" });
        }
        if (effect.kind === "proposal_append" && effect.reviewers) {
            if (effect.reviewerUserId)
                ctx.addIssue({ code: "custom", path: ["effects", index, "reviewers"], message: "Declare reviewerUserId or reviewers, not both" });
            if (new Set(effect.reviewers.userIds).size !== effect.reviewers.userIds.length)
                ctx.addIssue({ code: "custom", path: ["effects", index, "reviewers", "userIds"], message: "Each reviewer is named once" });
            const field = effect.reviewers.fromField === undefined ? null : definition.datasets
                .find((dataset) => dataset.key === effect.datasetKey)?.fields.find((candidate) => candidate.key === effect.reviewers.fromField);
            if (field !== null && field?.type !== "user")
                ctx.addIssue({ code: "custom", path: ["effects", index, "reviewers", "fromField"],
                    message: "A reviewer comes from a user field of the review's dataset" });
        }
        if (effect.kind === "source_read" && effect.pattern?.amountType) {
            // The read that says which documents' amounts include tax runs before this one, on the same step.
            const bindings = definition.effectBindings;
            const own = bindings.findIndex((binding) => binding.effectKey === effect.key);
            const other = bindings.findIndex((binding) => binding.effectKey === effect.pattern.amountType.effectKey);
            const read = definition.effects.find((candidate) => candidate.key === effect.pattern.amountType.effectKey);
            if (read?.kind !== "source_read" || own < 0 || other < 0 || other > own || bindings[other].nodeId !== bindings[own].nodeId)
                ctx.addIssue({ code: "custom", path: ["effects", index, "pattern", "amountType", "effectKey"],
                    message: "The amount type comes from a source read bound earlier on the same step" });
        }
        if (effect.kind === "proposal_append")
            (effect.judgeQuestions ?? []).forEach((question, at) => {
                const path = ["effects", index, "judgeQuestions", at];
                const fields = definition.datasets.find((dataset) => dataset.key === effect.datasetKey)?.fields ?? [];
                if (effect.judgeQuestions.findIndex((other) => other.id === question.id) !== at)
                    ctx.addIssue({ code: "custom", path: [...path, "id"], message: `Duplicate question '${question.id}'` });
                if (question.about !== null && !fields.some((field) => field.key === question.about && field.owner === "workflow"))
                    ctx.addIssue({ code: "custom", path: [...path, "about"],
                        message: "A question is about a workflow-owned field of the review's dataset, or the whole review (null)" });
                for (const property of ["ask", "instructions"])
                    for (const name of workflowJudgeQuestionPlaceholders(question[property] ?? "")) {
                        if (name.startsWith("pattern:")) {
                            const read = definition.effects.find((candidate) => candidate.key === name.slice("pattern:".length));
                            if ((read?.kind !== "source_read" && read?.kind !== "review_history") || !read.pattern)
                                ctx.addIssue({ code: "custom", path: [...path, property],
                                    message: `'{${name}}' must name a source read or review history that asks for a pattern` });
                            continue;
                        }
                        if (name === "value" ? question.about === null : !fields.some((field) => field.key === name))
                            ctx.addIssue({ code: "custom", path: [...path, property], message: name === "value"
                                    ? "{value} needs a question about a field" : `Unknown field '{${name}}'` });
                    }
                const answers = question.answer.kind === "yes_no" ? ["yes", "no"] : question.answer.options;
                if (new Set(answers).size !== answers.length)
                    ctx.addIssue({ code: "custom", path: [...path, "answer", "options"], message: "Duplicate option" });
                if (question.expects !== undefined && !answers.includes(question.expects))
                    ctx.addIssue({ code: "custom", path: [...path, "expects"], message: "The expected answer must be one the question can give" });
            });
        if (effect.kind === "proposal_append" && effect.waitForDecision) {
            const binding = definition.effectBindings.find(binding => binding.effectKey === effect.key);
            if (binding && definition.effectBindings.filter(candidate => candidate.nodeId === binding.nodeId).at(-1)?.effectKey !== effect.key)
                ctx.addIssue({ code: "custom", path: ["effects", index], message: "A waiting review must be the final effect on its node" });
        }
        if ("sourceKey" in effect && !sources.has(effect.sourceKey))
            ctx.addIssue({ code: "custom", path: ["effects", index, "sourceKey"], message: "Unknown source" });
        if ("datasetKey" in effect && !datasets.has(effect.datasetKey))
            ctx.addIssue({ code: "custom", path: ["effects", index, "datasetKey"], message: "Unknown dataset" });
        if (effect.kind === "source_read" && effect.contextKey) {
            const context = definition.context.find((candidate) => candidate.key === effect.contextKey);
            if (!context || context.sourceKey !== effect.sourceKey)
                ctx.addIssue({ code: "custom", path: ["effects", index, "contextKey"],
                    message: "Context read must name a declared context for this source" });
        }
        if (effect.kind === "review_history") {
            const field = definition.datasets.find(d => d.key === effect.datasetKey)?.fields.find(f => f.key === effect.matchFieldKey);
            if (!field || field.owner !== "workflow" || field.scope !== "item" || field.type !== "text" || field.projection)
                ctx.addIssue({ code: "custom", path: ["effects", index, "matchFieldKey"],
                    message: "Review history must match a declared workflow-owned item text field" });
            const lines = effect.pattern && definition.datasets.find(d => d.key === effect.datasetKey)?.fields
                .find(f => f.key === effect.pattern.fieldKey);
            if (effect.pattern && (!lines || lines.owner !== "workflow"))
                ctx.addIssue({ code: "custom", path: ["effects", index, "pattern", "fieldKey"],
                    message: "A review history pattern must read a declared workflow-owned field" });
        }
        if (effect.kind === "item_upsert") {
            const fields = fieldsByDataset.get(effect.datasetKey);
            if (new Set(effect.fieldKeys).size !== effect.fieldKeys.length)
                ctx.addIssue({ code: "custom", path: ["effects", index, "fieldKeys"], message: "Duplicate field key" });
            effect.fieldKeys.forEach((fieldKey, offset) => {
                if (fields && fields.get(fieldKey)?.owner !== "workflow")
                    ctx.addIssue({ code: "custom", path: ["effects", index, "fieldKeys", offset], message: "Item upsert may write only workflow-owned fields" });
                else if (fields?.get(fieldKey)?.scope === "line")
                    ctx.addIssue({ code: "custom",
                        path: ["effects", index, "fieldKeys", offset],
                        message: "Line-level findings belong in a targeted review proposal, not an item upsert" });
                else if (["review_state", "processing_state"].includes(definition.datasets
                    .find((dataset) => dataset.key === effect.datasetKey)?.fields.find((field) => field.key === fieldKey)?.projection ?? ""))
                    ctx.addIssue({ code: "custom", path: ["effects", index, "fieldKeys", offset],
                        message: "A projected review state is controlled by the decision ledger" });
            });
        }
    });
    const bindings = new Set();
    definition.effectBindings.forEach((binding, index) => {
        if (!nodes.has(binding.nodeId))
            ctx.addIssue({ code: "custom", path: ["effectBindings", index, "nodeId"], message: "Unknown node" });
        if (!effects.has(binding.effectKey))
            ctx.addIssue({ code: "custom", path: ["effectBindings", index, "effectKey"], message: "Undeclared effect" });
        if (binding.payloadKey && !declaresOutputProperty(definition.nodes.find((node) => node.id === binding.nodeId)?.outputSchema, binding.payloadKey))
            ctx.addIssue({ code: "custom", path: ["effectBindings", index, "payloadKey"],
                message: "Conditional effect needs a declared output property" });
        if (bindings.has(binding.effectKey))
            ctx.addIssue({ code: "custom", path: ["effectBindings", index, "effectKey"], message: "Effect already bound" });
        bindings.add(binding.effectKey);
    });
    definition.effects.forEach((effect, index) => {
        if (!bindings.has(effect.key))
            ctx.addIssue({ code: "custom", path: ["effects", index, "key"], message: "Declared effect must be bound to a node" });
    });
});
/**
 * A review step's reviewers in one shape, whichever way the step declared them: `reviewers`, or one
 * `reviewerUserId`. Null when the step names none, so the activation's approver reviews.
 */
export function workflowStepReviewers(effect) {
    if (effect.reviewers)
        return effect.reviewers;
    return effect.reviewerUserId ? { userIds: [effect.reviewerUserId] } : null;
}
